import {
  AimOutlined,
  BulbOutlined,
  ClockCircleOutlined,
  DotChartOutlined,
  FileOutlined,
  FireOutlined,
  FlagOutlined,
  GlobalOutlined,
  LinkOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { GET_IDEA, GET_ME, Idea, Me, TEAM_INVITE } from '@frontend/data-access';
import { App, Avatar, Button, Col, Row, Space, Tooltip } from 'antd';
import { NotificationInstance } from 'antd/lib/notification/interface';
import Link from 'next/link';
import { Translate } from 'next-translate';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import { handleOpenDocument } from '../documents/documents';
import IconCard from '../icon-card/icon-card';
import { IdeaDetailTeamInviteButton } from '../idea-detail/invite-button';
import UserAvatar from '../user-avatar/user-avatar';

export interface IdeaDetailBasicsProps {
  data: Idea;
}

interface IdeaBasicItemType {
  id: string;
  title: string;
  icon?: React.ReactNode;
  item: React.ReactNode;
  onClickJoin?: () => void;
}

export const getIdeaBasicItems = (
  idea: Idea,
  isUpdate = false,
  meData: Me | undefined,
  notification: NotificationInstance,
  tcommon: Translate,
  onClickJoin: () => void
): IdeaBasicItemType[] => {
  return [
    {
      title: 'applicants',
      icon: <TeamOutlined />,
      item: (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-5 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Avatar.Group max={{ count: 5 }} className="!space-x-2">
                {idea.anonymous ? (
                  <Avatar size={40} icon={<UserOutlined />} />
                ) : (
                  <UserAvatar size={40} user={idea.createdBy} showBadge />
                )}
                {idea.applicants
                  ?.filter(
                    (i) =>
                      i.status === 'APPROVED' && idea.createdBy.id !== i.user.id
                  )
                  ?.map((applicant, index) => (
                    <UserAvatar
                      key={index}
                      size={40}
                      user={applicant.user}
                      showBadge
                    />
                  ))}
              </Avatar.Group>
            </div>
            <IdeaDetailTeamInviteButton data={idea} />
          </div>
          {idea.collaborative && meData?.id !== idea.createdBy?.id && (
            <Tooltip
              title={
                idea.applicants.find((i) => i.user.id === meData?.id)
                  ? tcommon('idea_detail_join_team_pending')
                  : null
              }
            >
              <Button
                shape="round"
                onClick={onClickJoin}
                disabled={
                  idea.applicants.find((i) => i.user.id === meData?.id)
                    ? true
                    : false
                }
              >
                {tcommon('idea_detail_join_team')}
              </Button>
            </Tooltip>
          )}
        </div>
      ),
    },
    isUpdate || idea.videos?.length || idea.documents?.length
      ? {
          title: 'idea_detail_basics_attachments',
          icon: <FileOutlined />,
          item: (
            <div className="flex justify-between">
              <div className="truncate whitespace-nowrap overflow-hidden">
                {idea.videos?.length !== 0 &&
                  idea.videos?.map((item, index) => (
                    <div key={index}>
                      <span className="text-sm text-gray-900">Video</span>
                      <div className="space-x-1">
                        <span className="text-primary-600">
                          <LinkOutlined />
                        </span>
                        <Link
                          className="text-primary-600 break-all"
                          data-test="idea-video"
                          href={item}
                          target="_blank"
                        >
                          {item}
                        </Link>
                      </div>
                    </div>
                  ))}
                {idea?.documents && idea?.documents?.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-900">Documents</span>
                    {idea.documents?.map((file, index) => (
                      <div
                        className="space-x-1"
                        key={index}
                        data-test="idea-document"
                      >
                        <span className="text-primary-600">
                          <FileOutlined />
                        </span>
                        <span
                          className="text-primary-600 cursor-pointer"
                          onClick={() =>
                            handleOpenDocument(file, notification, tcommon)
                          }
                        >
                          {file.metadata?.filename}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ),
        }
      : null,
    idea.challenge
      ? {
          title: 'idea_detail_basics_challenge',
          icon: <BulbOutlined />,
          item: (
            <div>
              <Link
                data-test="idea-challenge-name"
                className="text-primary-600 text-wrap break-words whitespace-break-spaces"
                href={'/intrapreneurship/challenge/' + idea.challenge?.id || ''}
              >
                {idea.challenge?.title}
              </Link>
            </div>
          ),
        }
      : null,
    idea?.innovationSpaceTag
      ? {
          title: 'idea_detail_basics_innovation_space',
          icon: <FireOutlined />,
          item: (
            <div>
              <Link
                data-test="idea-challenge-name"
                className="text-primary-600 text-wrap break-words whitespace-break-spaces"
                href={`/innovation/${idea?.innovationSpaceTag?.id}`}
              >
                {idea?.innovationSpaceTag?.name}
              </Link>
            </div>
          ),
        }
      : null,
    {
      title: 'Region',
      icon: <GlobalOutlined />,
      item: (
        <Row className="gap-2 mt-1">
          {idea.regions?.map((item, index) => {
            return (
              <Col
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                key={index}
                data-test="idea-region"
              >
                {item.name}
              </Col>
            );
          })}
        </Row>
      ),
    },
    {
      title: 'idea_detail_basics_country',
      icon: <FlagOutlined />,
      item: (
        <Row className="gap-2 mt-1">
          {idea.countries?.map((item, index) => {
            return (
              <Col
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                key={index}
                data-test="idea-country"
              >
                {item.name}
              </Col>
            );
          })}
        </Row>
      ),
    },
    {
      title: 'idea_detail_basics_topics',
      icon: <TagsOutlined />,
      item: (
        <Row className="gap-2 mt-1">
          {idea.topics?.map((item, index) => {
            return (
              <Col
                className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                key={index}
                data-test="idea-topic"
              >
                {item.name}
              </Col>
            );
          })}
        </Row>
      ),
    },
    {
      title: 'idea_detail_basics_business_area',
      icon: <DotChartOutlined />,
      item: (
        <Row className="gap-2 mt-1">
          {idea.businessAreas?.map((item, index) => {
            return (
              <Col key={index} data-test="idea-business-area">
                {item}
              </Col>
            );
          })}
        </Row>
      ),
    },
    // {
    //   title: 'idea_detail_basics_idea_status',
    //   icon: <FireOutlined />,
    //   item: (
    //     <span data-test="idea-status">
    //       {idea.state && getLinkLabel(idea.state.toLowerCase())}
    //     </span>
    //   ),
    // },
    idea.drivers?.length
      ? {
          title: 'idea_detail_basics_strategic_driver',
          icon: <AimOutlined />,
          item: (
            <span data-test="idea-strategic-driver">
              {idea.drivers?.map((driver) => driver.driver).join(', ')}
            </span>
          ),
        }
      : null,
    idea.duration
      ? {
          title: 'idea_detail_basics_duration',
          icon: <ClockCircleOutlined />,
          item: <span data-test="idea-duration">{idea.duration}</span>,
        }
      : null,
  ].filter((a) => a != null) as IdeaBasicItemType[];
};

export function IdeaDetailBasics({ data }: IdeaDetailBasicsProps) {
  const { notification } = App.useApp();
  const { t: tcommon } = useTranslation('common');
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);

  const [inviteTeamMember] = useMutation(TEAM_INVITE, {
    refetchQueries: [
      {
        query: GET_IDEA,
        variables: {
          ideaId: data.id,
        },
      },
    ],
    onCompleted: () => {
      notification.success({
        message: tcommon('join_request_sent'),
      });
    },
  });

  const onClickJoin = async () => {
    await inviteTeamMember({
      variables: {
        ideaId: data.id,
        userId: meData?.me.id,
        type: 'JOIN',
      },
    });
  };

  return (
    <Space className="w-full" direction="vertical" size="middle">
      {getIdeaBasicItems(
        data,
        false,
        meData?.me,
        notification,
        tcommon,
        onClickJoin
      ).map((item, index) => (
        <IconCard key={index} title={tcommon(item.title)} icon={item.icon}>
          <div>{item.item}</div>
        </IconCard>
      ))}
    </Space>
  );
}

export default IdeaDetailBasics;
